import React, { useEffect, useState } from "react";
import {
  createVocabulary,
  updateVocabulary,
} from "./adminVocabularyService";
import "../../announcement/announcement.css";
import "../../styles/content.css";

const difficultyOptions = [
  "Beginner",
  "Elementary",
  "Intermediate",
  "Upper Intermediate",
  "Advanced",
  "Expert",
];

const partOfSpeechOptions = [
  { value: "noun", label: "Noun" },
  { value: "verb", label: "Verb" },
  { value: "adjective", label: "Adjective" },
  { value: "adverb", label: "Adverb" },
  { value: "pronoun", label: "Pronoun" },
  { value: "preposition", label: "Preposition" },
  { value: "conjunction", label: "Conjunction" },
  { value: "interjection", label: "Interjection" },
  { value: "determiner", label: "Determiner" },
];

const toList = (str) =>
  str
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);

const toStr = (arr) => (Array.isArray(arr) ? arr.join("\n") : arr || "");

const AdminVocabularyForm = ({ vocabulary, onSaved, onCancel }) => {
  const isEdit = !!vocabulary;

  const [form, setForm] = useState({
    word: "",
    pronunciation: "",
    phoneticSpelling: "",
    partOfSpeech: [],
    definition: "",
    examples: "",
    difficulty: "",
    synonyms: "",
    antonyms: "",
    youtubeVideoLink: "",
    etymology: "",
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (vocabulary) {
      setForm({
        word: vocabulary.word || "",
        pronunciation: vocabulary.pronunciation || "",
        phoneticSpelling: vocabulary.phoneticSpelling || "",
        partOfSpeech: Array.isArray(vocabulary.partOfSpeech) 
          ? vocabulary.partOfSpeech 
          : vocabulary.partOfSpeech 
            ? [vocabulary.partOfSpeech] 
            : [],
        definition: vocabulary.definition || "",
        examples: toStr(vocabulary.examples),
        difficulty: vocabulary.difficulty || "",
        synonyms: toStr(vocabulary.synonyms),
        antonyms: toStr(vocabulary.antonyms),
        youtubeVideoLink: vocabulary.youtubeVideoLink || "",
        etymology: vocabulary.etymology || "",
      });
    }
  }, [vocabulary]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePartOfSpeechAdd = (pos) => {
    if (!form.partOfSpeech.includes(pos)) {
      setForm({ ...form, partOfSpeech: [...form.partOfSpeech, pos] });
    }
  };

  const handlePartOfSpeechRemove = (pos) => {
    setForm({ 
      ...form, 
      partOfSpeech: form.partOfSpeech.filter(p => p !== pos) 
    });
  };

  const handleSubmit = async () => {
    if (!form.word || !form.definition || !form.difficulty) {
      alert("Word, Definition, and Difficulty are required");
      return;
    }

    const payload = {
      word: form.word,
      pronunciation: form.pronunciation,
      phoneticSpelling: form.phoneticSpelling,
      partOfSpeech: form.partOfSpeech,
      definition: form.definition,
      examples: toList(form.examples),
      difficulty: form.difficulty,
      synonyms: toList(form.synonyms),
      antonyms: toList(form.antonyms),
      ...(form.youtubeVideoLink && { youtubeVideoLink: form.youtubeVideoLink }),
      ...(form.etymology && { etymology: form.etymology }),
    };

    setLoading(true);
    const res = isEdit
      ? await updateVocabulary(vocabulary.id, payload)
      : await createVocabulary(payload);

    if (res.status === 200 || res.status === 201) {
      onSaved();
    } else {
      alert(res.message || "Operation failed");
    }
    setLoading(false);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="form-card" style={{ margin: 0, boxShadow: "none" }}>
          <div className="form-header">
            <h2>{isEdit ? "Edit Vocabulary" : "Create Vocabulary"}</h2>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>Word *</label>
              <input
                name="word"
                placeholder="Enter word"
                value={form.word}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Pronunciation</label>
              <input
                name="pronunciation"
                placeholder="e.g. /əˈbʌndəns/"
                value={form.pronunciation}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Phonetic Spelling</label>
              <input
                name="phoneticSpelling"
                placeholder="e.g. pruh-nuhn-see-AY-shuhn"
                value={form.phoneticSpelling}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Part of Speech</label>
              
              {/* Selected Parts of Speech as Chips */}
              {form.partOfSpeech.length > 0 && (
                <div style={{ marginBottom: '8px' }}>
                  {form.partOfSpeech.map((pos) => {
                    const option = partOfSpeechOptions.find(opt => opt.value === pos);
                    return (
                      <span 
                        key={pos}
                        style={{
                          display: 'inline-block',
                          background: '#e3f2fd',
                          color: '#1976d2',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          marginRight: '6px',
                          marginBottom: '4px',
                          cursor: 'pointer'
                        }}
                        onClick={() => handlePartOfSpeechRemove(pos)}
                        title="Click to remove"
                      >
                        {option ? option.label : pos} ×
                      </span>
                    );
                  })}
                </div>
              )}
              
              {/* Dropdown to Add Parts of Speech */}
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value) {
                    handlePartOfSpeechAdd(e.target.value);
                    e.target.value = "";
                  }
                }}
              >
                <option value="">Add Part of Speech</option>
                {partOfSpeechOptions
                  .filter(opt => !form.partOfSpeech.includes(opt.value))
                  .map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))
                }
              </select>
            </div>

            <div className="form-group">
              <label>Difficulty *</label>
              <select
                name="difficulty"
                value={form.difficulty}
                onChange={handleChange}
              >
                <option value="">Select Difficulty</option>
                {difficultyOptions.map((opt) => (
                  <option key={opt}>{opt}</option>
                ))}
              </select>
            </div>

            <div className="form-group full-width">
              <label>Definition *</label>
              <textarea
                name="definition"
                placeholder="Enter definition"
                value={form.definition}
                onChange={handleChange}
                rows={2}
              />
            </div>

            <div className="form-group full-width">
              <label>Examples (one per line)</label>
              <textarea
                name="examples"
                placeholder={"The room filled with laughter.\nLaughter is the best medicine."}
                value={form.examples}
                onChange={handleChange}
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>Synonyms (one per line)</label>
              <textarea
                name="synonyms"
                placeholder={"chuckling\ngiggling\nmirth"}
                value={form.synonyms}
                onChange={handleChange}
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>Antonyms (one per line)</label>
              <textarea
                name="antonyms"
                placeholder={"crying\nweeping\nsorrow"}
                value={form.antonyms}
                onChange={handleChange}
                rows={3}
              />
            </div>

            <div className="form-group full-width">
              <label>YouTube Video Link (optional)</label>
              <input
                name="youtubeVideoLink"
                placeholder="https://youtube.com/..."
                value={form.youtubeVideoLink}
                onChange={handleChange}
              />
            </div>

            <div className="form-group full-width">
              <label>Etymology (optional)</label>
              <textarea
                name="etymology"
                placeholder="Origin of the word..."
                value={form.etymology}
                onChange={handleChange}
                rows={2}
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              className="btn primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Saving..." : isEdit ? "Update" : "Create"}
            </button>
            <button
              className="btn secondary"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminVocabularyForm;
